import { connect } from 'react-redux';
import { useCallback } from 'react';
import { isAfter } from 'date-fns';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getKnmFormRef, getKnmOnSaveButtonClick } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { ResultDecisionsItemContentExtract } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentExtract/ResultDecisionsItemContentExtract';
import { IKnmPmFormResultDecision } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { ResultDecisionsItemContentSignButton } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentSignButton/ResultDecisionsItemContentSignButton';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ResultDecisionsItemContentGeneral } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentGeneral/ResultDecisionsItemContentGeneral';
import { ResultDecisionsItemContentViolations } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentViolations/ResultDecisionsItemContentViolations';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { ResultDecisionsItemContentSignInfo } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentSignInfo/ResultDecisionsItemContentSignInfo';
import { ResultDecisionsItemContentIntroductions } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentIntroductions/ResultDecisionsItemContentIntroductions';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { ResultDecisionsItemContentCancelInfo } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsItemPaperlesContent/components/ResultDecisionsItemContentCancelInfo/ResultDecisionsItemContentCancelInfo';
import { KnmResultItemSubjectTypesEnum } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultItemSubjectInfo';

import { ResultDecisionsPmItemContentSubject } from './components/ResultDecisionsItemContentSubject/ResultDecisionsItemContentSubject';
import { ResultDecisionsItemContentActs } from './components/ResultDecisionsItemContentActs/ResultDecisionsItemContentActs';
import { ResultDecisionsPmItemContentSubjectWithFounder } from './components/ResultDecisionsItemContentSubject/ResultDecisionsPmItemContentSubjectWithFounder';

interface IPmResultDecisionPrescriptionPaperlessProps {
  watchOnly: boolean;
  value: IKnmPmFormResultDecision;
  name: string;
  addRequirementsNpa: () => void;
  values?: IKnmPmForm;
  resultIndex: number;
  id: string;
  clearWatchOnly: boolean;
  isLater4111ForDecision: boolean;
  lateFieldName: string;
  isExpandAllInPrescription?: boolean;
  extractVersion?: number;
}

interface IPmResultDecisionPrescriptionPaperlessStoreProps {
  onSaveButtonClick: (() => void) | undefined;
  formRef: IKnmFormRef | undefined;
}

function PmResultDecisionPrescriptionPaperlessWithoutStore({
  onSaveButtonClick,
  value,
  watchOnly,
  formRef,
  name,
  addRequirementsNpa,
  values,
  resultIndex,
  id,
  clearWatchOnly,
  isLater4111ForDecision,
  lateFieldName,
  isExpandAllInPrescription,
  extractVersion,
}: IPmResultDecisionPrescriptionPaperlessProps & IPmResultDecisionPrescriptionPaperlessStoreProps) {
  const violatedMandatoryRequirements = formRef?.current?.getFieldState(
    `${name}.violatedMandatoryRequirements` as keyof IKnmPmForm,
  )?.value as IKnmErknmFormViolatedRequirementsNpa[] | undefined;

  const isSecondSign = typeof extractVersion === 'number' && extractVersion > 0;

  const isDisabledField = clearWatchOnly || !!value.signed || isSecondSign;

  const isSignButtonDisabled = clearWatchOnly || !!value.signed;

  const onChangeFixPlanDate = useCallback(() => {
    let maxDate = '';

    violatedMandatoryRequirements?.forEach(vmr => {
      vmr.violatedMandatory?.forEach(vm => {
        vm.violations?.forEach(v => {
          if (v.fixPlanDates?.length && v.fixPlanDates[0].value) {
            const currentDate = parseDate({
              date: v.fixPlanDates[0].value,
              parseFormat: DATE_FORMAT_LONG_FNS,
            });
            const maxDateFormated = parseDate({ date: maxDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

            if (!maxDate || isAfter(currentDate || 0, maxDateFormated || 0)) {
              maxDate = v.fixPlanDates[0].value;
            }
          }
        });
      });
    });

    if (maxDate) {
      formRef?.current?.change(`${name}.deadline` as keyof IKnmPmForm, maxDate);
    }
  }, [formRef, violatedMandatoryRequirements]);

  return (
    <>
      <ResultDecisionsItemContentActs values={values} />
      <ResultDecisionsItemContentExtract attachment={value.attachment} />
      <ResultDecisionsItemContentGeneral
        formRef={formRef}
        isDisabledField={isDisabledField}
        dateName={`${name}.date`}
        dateResolvedName={`${name}.deadline`}
        infoResolvedName={`${name}.information`}
        infoResolvedId={`${id}.information`}
        isHideInfoResolved={values?.act?.isViolatedMandatoryRequirements?.id === 1}
        isPm
        name={name}
        agreementStatus={value.agreementStatus}
      />
      {value.organization?.subjectTypePm?.code &&
      isLater4111ForDecision &&
      value.organization.subjectTypePm.code === KnmResultItemSubjectTypesEnum.UR ? (
        <ResultDecisionsPmItemContentSubjectWithFounder
          name={name}
          id={id}
          value={value}
          lateFieldName={lateFieldName}
        />
      ) : (
        <ResultDecisionsPmItemContentSubject name={name} id={id} value={value} />
      )}
      <ResultDecisionsItemContentViolations
        addRequirementsNpa={addRequirementsNpa}
        actsRequirementsNpa={values?.act?.violatedMandatoryRequirements || []}
        decisionPmIndex={resultIndex}
        name={name}
        formRef={formRef}
        watchOnly={watchOnly}
        clearWatchOnly={clearWatchOnly}
        isPaperless={!!value.paperless}
        onChangeFixPlanDate={onChangeFixPlanDate}
        id={id}
        isPm
        signed={value.signed}
        isExpandAllInPrescription={isExpandAllInPrescription}
        extractVersion={extractVersion}
      />
      <ResultDecisionsItemContentSignInfo
        isDisabledField={isDisabledField}
        hasResultDecisionFirstExtractDateTime={!!value?.firstExtractDateTime}
        fioSignerFieldName={`${name}.signerFullName`}
        titleSignerFieldName={`${name}.signerPosition`}
        fioSignerId={`${id}.signerFullName`}
        titleSignerId={`${id}.signerPosition`}
        isPm
        isNew={value?.isNew === true}
        formRef={formRef}
        kindCode={value?.kindDecision?.code}
        name={name}
        resultDecisionId={id}
        isSigned={value?.signed === true}
        resultDecisionKindCode={value.kindDecision?.code}
      />
      {value.attachment && <ResultDecisionsItemContentIntroductions id={id} name={name} formRef={formRef} isPm />}
      <ResultDecisionsItemContentSignButton
        isDisabledField={isSignButtonDisabled}
        value={value}
        formRef={formRef}
        onSaveButtonClick={onSaveButtonClick}
        isResultDecision
        decisionIndex={resultIndex}
        isPm
        name={name}
      />
      <ResultDecisionsItemContentCancelInfo
        cancelDocument={value.cancelDocument}
        cancelCommentFieldName={`${name}.cancelComment`}
        fioSignerFieldName={`${name}.cancelFioSigner`}
        titleSignerFieldName={`${name}.cancelTitleSigner`}
        value={value}
        isPm
        isCancelSigned={value.isCancelSigned}
      />
    </>
  );
}

const mapStateToProps = (state: IRootState): IPmResultDecisionPrescriptionPaperlessStoreProps => ({
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  formRef: getKnmFormRef(state),
});

export const PmResultDecisionPrescriptionPaperless = connect(mapStateToProps)(
  PmResultDecisionPrescriptionPaperlessWithoutStore,
);
